import endOfDay from 'date-fns/endOfDay';
import isWithinInterval from 'date-fns/isWithinInterval';
import startOfDay from 'date-fns/startOfDay';
import { defineMessage, IntlShape } from 'react-intl';
import { NormalOperatingHours, SpecialOperatingHours, TimeOpenClosed } from '../generated/graphql';
import { getWeekday } from './weekday';

export type Weekday = keyof Omit<NormalOperatingHours, '__typename'>;

const timeStrToMinutes = (time: string) => {
    const [hours, minutes] = time.split(':');
    return Number(hours) * 60 + Number(minutes);
};

const getRelevantDayTime = (dayOpeningTimes: TimeOpenClosed[]) => {
    const curDate = new Date();
    const curTimeInMin = timeStrToMinutes(`${curDate.getHours()}:${curDate.getMinutes()}`);
    const closedTimesInMin = dayOpeningTimes.map(t => timeStrToMinutes(t.timeClosed));

    let lastTimeIndex = closedTimesInMin.length - 1;

    if (closedTimesInMin.length > 1) {
        closedTimesInMin.some((t, i) => {
            if (curTimeInMin < t) {
                lastTimeIndex = i;
                return true;
            }
            return false;
        });
    }

    return dayOpeningTimes[lastTimeIndex];
};
export const openMessage = defineMessage({
    defaultMessage: 'Open van {open} tot {close}',
});

export const alwaysOpenMessage = defineMessage({
    defaultMessage: 'Altijd open',
});

/**
 * This function returns the relevant time for a given date.
 *
 * Some examples:
 * If there are special opening hours for the given date, it will return the special opening hours.
 * If there are no special opening hours, it will return the normal opening hours.
 * If there are no normal opening hours, it will return 'Gesloten'.
 * If the opening hours are 00:00 - 23:59, it will return 'Altijd open'.
 *
 * @param intl - The intl object from react-intl
 * @param weekTimes - The normal opening hours
 * @param specialOpeningHours - The special opening hours
 * @param date - The date for which you want to get the relevant time
 * @returns - The relevant time for the given date
 */
const getRelevantTime = (
    intl: IntlShape,
    weekTimes: NormalOperatingHours | null | undefined,
    specialOpeningHours: SpecialOperatingHours[] | null | undefined,
    date = new Date()
) => {
    const curDate = date;
    const day = getWeekday(date);

    if (specialOpeningHours) {
        for (const s of specialOpeningHours) {
            const startDate = startOfDay(new Date(s.startDate));
            const endDate = endOfDay(new Date(s.endDate));
            const sohToday = s[day];
            if (sohToday && isWithinInterval(curDate, { start: startDate, end: endDate })) {
                const timeOpen = getRelevantDayTime(sohToday)?.timeOpen;
                const timeClosed = getRelevantDayTime(sohToday)?.timeClosed;
                if (timeOpen === '00:00' && timeClosed === '23:59') {
                    return intl.formatMessage(alwaysOpenMessage);
                }
                return sohToday.length > 0
                    ? intl.formatMessage(openMessage, {
                          open: timeOpen,
                          close: timeClosed,
                      })
                    : intl.formatMessage({
                          defaultMessage: 'Gesloten',
                      });
            }
        }
    }

    if (weekTimes) {
        const wtToday = weekTimes[day];

        if (wtToday && wtToday.length > 0) {
            const { timeOpen } = getRelevantDayTime(wtToday);
            const { timeClosed } = getRelevantDayTime(wtToday);

            if (timeOpen === '00:00' && timeClosed === '23:59') {
                return intl.formatMessage(alwaysOpenMessage);
            }
            return intl.formatMessage(openMessage, {
                open: timeOpen,
                close: timeClosed,
            });
        }
    }

    return intl.formatMessage({
        defaultMessage: 'Gesloten',
    });
};

export default getRelevantTime;
